import React, { useEffect, useState, useMemo } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import { db, functions } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import './Lenders.css';

interface LenderRow {
  nmlsId: string;
  fullName: string;
  nmlsType: string;
  companyName: string;
  city: string;
  zipCode: string;
  state: string;
  experienceYears: number | null;
  officeName: string;
}

/** Returns the PTC experience tier label matching the ptcCalculator buckets */
function getExperienceTier(years: number): { label: string; className: string } {
  if (years <= 1) return { label: 'Entry', className: 'tier-entry' };
  if (years <= 3) return { label: 'Junior', className: 'tier-junior' };
  if (years <= 5) return { label: 'Mid-Level', className: 'tier-mid' };
  if (years < 10) return { label: 'Senior', className: 'tier-senior' };
  return { label: 'Expert', className: 'tier-expert' };
}

const Lenders: React.FC = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const isAdmin = userProfile?.role === 'admin';
  const [lenders, setLenders] = useState<LenderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const [pendingData, setPendingData] = useState<{ pendingLenders: unknown[]; totalOffersNmlsIds: number; totalEnriched: number } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [expFilter, setExpFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch lenders with verified employment history (backfilled by backfill-lender-experience.js)
        const lendersSnap = await getDocs(
          query(collection(db, 'lenders'), where('employmentHistory', '==', true))
        );

        const rows: LenderRow[] = [];
        lendersSnap.forEach(docSnap => {
          const d = docSnap.data();

          const fullName = [
            d.individualName?.firstName,
            d.individualName?.lastName,
          ].filter(Boolean).join(' ') || d.name || '';
          if(d.nmlsId == "1892112") {
            console.log('Processing lender:', d);  
          }
          rows.push({
            nmlsId: d.nmlsId?.toString() || docSnap.id,
            fullName,
            nmlsType: d.nmlsType || '',
            companyName: d.companyName || '',
            city: d.city || '',
            zipCode: d.zipCode || '',
            state: d.state || d.stateAbbreviation || '',
            experienceYears: d.yearsExperience ?? null,
            officeName: d.companyName || '',
          });
        });

        // Sort by experience descending
        rows.sort((a, b) => (b.experienceYears ?? 0) - (a.experienceYears ?? 0));
        setLenders(rows);
      } catch (err) {
        console.error('Error fetching lender data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    const fetchPendingCount = async () => {
      try {
        const fn = httpsCallable<unknown, { success: boolean; pendingLenders: unknown[]; totalOffersNmlsIds: number; totalEnriched: number }>(functions, 'getPendingLenders');
        const result = await fn();
        if (result.data.success) {
          setPendingCount(result.data.pendingLenders.length);
          setPendingData({
            pendingLenders: result.data.pendingLenders,
            totalOffersNmlsIds: result.data.totalOffersNmlsIds,
            totalEnriched: result.data.totalEnriched,
          });
        }
      } catch {
        setPendingCount(0);
      }
    };
    fetchPendingCount();
  }, [isAdmin]);

  const uniqueStates = useMemo(
    () => Array.from(new Set(lenders.map(l => l.state).filter(Boolean))).sort(),
    [lenders]
  );

  const filtered = useMemo(() => {
    let list = lenders;

    if (stateFilter) {
      list = list.filter(l => l.state === stateFilter);
    }

    if (expFilter) {
      list = list.filter(l =>
        l.experienceYears !== null && getExperienceTier(l.experienceYears).label === expFilter
      );
    }

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      list = list.filter(l =>
        l.fullName.toLowerCase().includes(q) ||
        l.companyName.toLowerCase().includes(q) ||
        l.officeName.toLowerCase().includes(q) ||
        l.nmlsId.includes(q) ||
        l.city.toLowerCase().includes(q)
      );
    }

    return list;
  }, [lenders, stateFilter, expFilter, searchTerm]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginated = filtered.slice(startIndex, startIndex + itemsPerPage);

  const handleClearFilters = () => {
    setSearchTerm('');
    setStateFilter('');
    setExpFilter('');
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push('...');
      pages.push(totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(1);
      pages.push('...');
      for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push('...');
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const tiers = ['Entry', 'Junior', 'Mid-Level', 'Senior', 'Expert'];

  return (
    <div className="lenders-container">
      <div className="lenders-header">
        <div>
          <h1>Lenders Directory</h1>
          <p className="total-count">
            {loading
              ? 'Loading...'
              : `Showing ${Math.min(startIndex + 1, filtered.length)}–${Math.min(startIndex + itemsPerPage, filtered.length)} of ${filtered.length} lenders with verified experience`}
          </p>
        </div>
        {isAdmin && (
          <button
            className="pending-lenders-btn"
            onClick={() => navigate('/pending-lenders', { state: { prefetched: pendingData } })}
          >
            Pending Lenders
            {pendingCount !== null && pendingCount > 0 && (
              <span className="pending-lenders-badge">{pendingCount}</span>
            )}
          </button>
        )}
      </div>

      <div className="lenders-filters">
        <div className="filter-group" style={{ flex: 2 }}>
          <input
            type="text"
            placeholder="Search by name, company, NMLS #, city..."
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <select
            value={stateFilter}
            onChange={e => { setStateFilter(e.target.value); setCurrentPage(1); }}
            className="filter-select"
          >
            <option value="">All States</option>
            {uniqueStates.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <select
            value={expFilter}
            onChange={e => { setExpFilter(e.target.value); setCurrentPage(1); }}
            className="filter-select"
          >
            <option value="">All Experience Levels</option>
            {tiers.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        {(searchTerm || stateFilter || expFilter) && (
          <button onClick={handleClearFilters} className="clear-filters-btn">
            Clear
          </button>
        )}
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading lenders...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🔍</div>
          <p>No lenders found</p>
          <span>Try adjusting your filters</span>
        </div>
      ) : (
        <div className="table-container">
          <table className="lenders-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>NMLS #</th>
                <th>Type</th>
                <th>Company</th>
                <th>Office Name</th>
                <th>City</th>
                <th>State</th>
                <th>ZIP</th>
                <th>Experience</th>
                <th>Level</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map(lender => {
                const tier = lender.experienceYears !== null
                  ? getExperienceTier(lender.experienceYears)
                  : null;
                return (
                  <tr key={lender.nmlsId}>
                    <td className="name-cell">{lender.fullName || '—'}</td>
                    <td className="nmls-cell">{lender.nmlsId}</td>
                    <td className="type-cell">
                      {lender.nmlsType
                        ? <span className="type-badge">{lender.nmlsType}</span>
                        : '—'}
                    </td>
                    <td className="company-cell">{lender.companyName || '—'}</td>
                    <td className="company-cell">{lender.officeName || '—'}</td>
                    <td>{lender.city || '—'}</td>
                    <td className="state-cell">{lender.state || '—'}</td>
                    <td className="zip-cell">{lender.zipCode || '—'}</td>
                    <td className="exp-cell">
                      {lender.experienceYears === null ? '—'
                        : lender.experienceYears < 1
                          ? `${Math.round(lender.experienceYears * 12)} mo`
                          : `${lender.experienceYears.toFixed(1)} yrs`}
                    </td>
                    <td>
                      {tier && (
                        <span className={`exp-tier-badge ${tier.className}`}>{tier.label}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && filtered.length > 0 && totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            ← Previous
          </button>

          <div className="pagination-numbers">
            {getPageNumbers().map((page, index) =>
              page === '...' ? (
                <span key={`e-${index}`} className="pagination-ellipsis">...</span>
              ) : (
                <button
                  key={page}
                  className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                  onClick={() => handlePageChange(page as number)}
                >
                  {page}
                </button>
              )
            )}
          </div>

          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default Lenders;
